import { z } from "zod";
import { toDisplayDateInput } from "./dateUtils";

/**
 * Phone number validation regex
 * Allows: digits, spaces, dashes, dots, parentheses for formatting
 * Validates: exactly 10 digits after removing formatting characters
 */
const PHONE_NUMBER_PATTERN = /^[0-9\s\-().]+$/;

/**
 * Validates phone number format and length
 * @param value - Phone number string
 * @returns true if valid, false otherwise
 */
const isValidPhoneNumber = (value: string): boolean => {
  // Remove all non-digit characters to count digits
  const digitsOnly = value.replace(/\D/g, "");
  // Check if it has exactly 10 digits
  if (digitsOnly.length !== 10) {
    return false;
  }
  // Check if the value contains only valid phone number characters (no plus sign for 10-digit numbers)
  return PHONE_NUMBER_PATTERN.test(value);
};

/**
 * Phone number validation schema for required fields (create forms)
 * Validates: exactly 10 digits, allows formatting characters
 */
export const phoneNumberSchema = z
  .string()
  .min(1, "Phone number is required")
  .refine(isValidPhoneNumber, {
    message: "Please enter a valid phone number (10 digits)",
  });

/**
 * Phone number validation schema for optional fields (update forms)
 * If value is provided, validates it; if empty/undefined, allows it
 */
export const phoneNumberOptionalSchema = z
  .string()
  .optional()
  .refine(
    (value) => {
      // If value is provided, validate it; if empty/undefined, allow it
      if (!value || value.trim() === "") return true;
      return isValidPhoneNumber(value);
    },
    {
      message: "Please enter a valid phone number (10 digits)",
    }
  );

/**
 * Date validation utilities
 */

/**
 * Gets today's date in YYYY-MM-DD format (without time)
 *
 * Uses the display timezone rather than `setHours` + `toISOString`: that pair
 * snapped to local midnight and then converted to UTC, which for any zone ahead
 * of UTC lands on the previous day — so this returned *yesterday* for every IST
 * user, every time, and "not in the past" checks accepted a stale date.
 *
 * @returns Today's date as YYYY-MM-DD string
 */
const getTodayDateString = (): string => toDisplayDateInput();

/**
 * Compares two dates in YYYY-MM-DD format, treating equal dates as valid
 * @param date1 - First date string
 * @param date2 - Second date string
 * @returns true if date1 is the same day as, or after, date2
 */
const isDateOnOrAfter = (date1: string, date2: string): boolean => {
  return new Date(date1) >= new Date(date2);
};

/**
 * Validates that a start date is not in the past (today is allowed)
 *
 * Today counts as valid: the backend's flight window is inclusive and
 * day-granular (see `creativeWindow.ts`), so a creative dated today is live
 * today. Requiring strictly-after-today made the portal refuse a window the
 * server would have honoured, forcing every "start now" creative to wait a day.
 *
 * @param startDate - Start date string (YYYY-MM-DD format)
 * @returns true if valid, false otherwise
 */
const isStartDateValid = (startDate: string | undefined): boolean => {
  if (!startDate || startDate.trim() === "") return true;
  const today = getTodayDateString();
  return isDateOnOrAfter(startDate, today);
};

/**
 * Validates that an end date is not before the start date
 *
 * Equal dates are valid — that is a one-day flight, which the backend's
 * inclusive day-granular window (`creativeWindow.ts`) plays for exactly one
 * day. Requiring strictly-after made a single-day creative impossible to enter.
 *
 * @param startDate - Start date string (YYYY-MM-DD format)
 * @param endDate - End date string (YYYY-MM-DD format)
 * @returns true if valid, false otherwise
 */
const isEndDateValid = (
  startDate: string | undefined,
  endDate: string | undefined
): boolean => {
  if (!startDate || startDate.trim() === "") return true;
  if (!endDate || endDate.trim() === "") return true;
  return isDateOnOrAfter(endDate, startDate);
};

/**
 * Whether two optional date-input values represent the same value
 * @param a - First date string
 * @param b - Second date string
 * @returns true if both are the same date (or both empty)
 */
const isSameDateValue = (a: string | undefined, b: string | undefined): boolean =>
  (a ?? "").trim() === (b ?? "").trim();

/**
 * Date range validation schema refinement
 *
 * For CREATE forms:
 * - Start date must be today or later (if provided)
 * - End date must be on or after start date (if both provided)
 *
 * For UPDATE forms:
 * - Start date must be today or later, but only if the operator changed it —
 *   an existing creative's own past start date stays editable
 * - End date must be on or after start date (if both provided)
 *
 * Use this with .refine() on your schema:
 * @example
 * // For create forms
 * const createSchema = z.object({ startDate: z.string().optional(), endDate: z.string().optional() })
 *   .refine(...dateRangeValidation.create.startDateNotInPast())
 *   .refine(...dateRangeValidation.create.endDateNotBeforeStartDate())
 *
 * // For update forms — seeded with the row's current start date
 * const updateSchema = z.object({ startDate: z.string().optional(), endDate: z.string().optional() })
 *   .refine(...dateRangeValidation.update.startDateNotInPastIfChanged(original))
 *   .refine(...dateRangeValidation.update.endDateNotBeforeStartDate())
 */
export const dateRangeValidation = {
  /**
   * Validation rules for CREATE forms
   */
  create: {
    /**
     * Validates that start date is today or later (for create forms)
     */
    startDateNotInPast: <T extends { startDate?: string }>(): [
      (data: T) => boolean,
      { message: string; path: string[] }
    ] => [
      (data: T) => isStartDateValid(data.startDate),
      {
        message: "Start date must be today or a future date",
        path: ["startDate"],
      },
    ],

    /**
     * Validates that end date is on or after start date
     */
    endDateNotBeforeStartDate: <
      T extends { startDate?: string; endDate?: string }
    >(): [(data: T) => boolean, { message: string; path: string[] }] => [
      (data: T) => isEndDateValid(data.startDate, data.endDate),
      {
        message: "End date must be on or after start date",
        path: ["endDate"],
      },
    ],
  },

  /**
   * Validation rules for UPDATE forms
   */
  update: {
    /**
     * Validates a start date only when the operator actually changed it.
     *
     * An already-running or expired creative legitimately carries a start date
     * in the past, so applying the create rule wholesale would make its own
     * stored value invalid — an operator could not touch the description
     * without being forced to move the flight window. Comparing against the
     * value the form opened with confines the rule to a *new* choice, which is
     * the only case where "don't backdate" is a meaningful instruction.
     *
     * @param originalStartDate - The start date the form was seeded with
     *   (YYYY-MM-DD, or empty when the creative has none)
     */
    startDateNotInPastIfChanged: <T extends { startDate?: string }>(
      originalStartDate?: string
    ): [(data: T) => boolean, { message: string; path: string[] }] => [
      (data: T) =>
        isSameDateValue(data.startDate, originalStartDate) ||
        isStartDateValid(data.startDate),
      {
        message: "Start date must be today or a future date",
        path: ["startDate"],
      },
    ],

    /**
     * Validates that end date is on or after start date (for update forms)
     */
    endDateNotBeforeStartDate: <
      T extends { startDate?: string; endDate?: string }
    >(): [(data: T) => boolean, { message: string; path: string[] }] => [
      (data: T) => isEndDateValid(data.startDate, data.endDate),
      {
        message: "End date must be on or after start date",
        path: ["endDate"],
      },
    ],
  },
};
